// Cavilow template catalog. 50+ combinations built from 10 base layouts × color/font families.
// All templates are original Cavilow designs — no third-party template code.

const CATEGORY = {
  ATS: "ATS-Friendly",
  MODERN: "Modern Professional",
  EXEC: "Executive",
  FINANCE: "Finance & Accounting",
  SALES: "Sales & Marketing",
  HR: "HR & Administration",
  IT: "IT / Data / Technology",
  FRESHER: "Freshers / Entry Level",
  GCC: "UAE / GCC Professional",
  CREATIVE: "Creative / Portfolio Style",
};

// Layouts — each id maps to a component in TemplateRenderer.jsx
export const LAYOUTS = {
  ATLAS: "atlas",           // Single-column, left blue accent bar, modern sans
  MERIDIAN: "meridian",     // Two-column (sidebar + main), balanced
  KEYSTONE: "keystone",     // Classic serif, centered header, ATS-friendly
  APEX: "apex",             // Bold header block, sans-serif, exec feel
  CANVAS: "canvas",         // Minimalist mono headings, technical
  PRISM: "prism",           // Colored side rail with photo, creative
  SIGNAL: "signal",         // Top ribbon header, single column, punchy
  HERITAGE: "heritage",     // Formal serif, two-column, executive
  PULSE: "pulse",           // Compact one-page, dense, ATS-friendly
  ORBIT: "orbit",           // Centered elegant, photo circle, GCC
};

const F = {
  sans: { display: "Outfit, ui-sans-serif, system-ui", body: "Manrope, ui-sans-serif, system-ui" },
  serif: { display: "Georgia, 'Times New Roman', serif", body: "Georgia, 'Times New Roman', serif" },
  mixed: { display: "Outfit, ui-sans-serif, system-ui", body: "Georgia, 'Times New Roman', serif" },
  mono: { display: "'IBM Plex Mono', ui-monospace, monospace", body: "Manrope, ui-sans-serif, system-ui" },
};

// Helper to build a template row
const T = (id, name, layout, accent, cats, opts = {}) => ({
  id,
  name,
  layout,
  accent,
  categories: cats,
  ats_friendly: opts.ats ?? cats.includes(CATEGORY.ATS),
  pages: opts.pages ?? 1,
  experience_levels: opts.exp ?? ["mid", "senior"],
  industries: opts.industries ?? [],
  font_pair: opts.font ?? F.sans,
  supports_photo: opts.photo ?? false,
});

// 50+ templates
export const TEMPLATES = [
  // === Atlas (single-column left accent bar) — 6 colors ===
  T("atlas-blue", "Atlas · Blue", LAYOUTS.ATLAS, "#1E6FE6", [CATEGORY.ATS, CATEGORY.MODERN, CATEGORY.IT]),
  T("atlas-teal", "Atlas · Teal", LAYOUTS.ATLAS, "#0EA5A2", [CATEGORY.ATS, CATEGORY.MODERN, CATEGORY.HR]),
  T("atlas-plum", "Atlas · Plum", LAYOUTS.ATLAS, "#7C3AED", [CATEGORY.ATS, CATEGORY.MODERN, CATEGORY.SALES]),
  T("atlas-slate", "Atlas · Slate", LAYOUTS.ATLAS, "#334155", [CATEGORY.ATS, CATEGORY.FINANCE]),
  T("atlas-emerald", "Atlas · Emerald", LAYOUTS.ATLAS, "#059669", [CATEGORY.ATS, CATEGORY.MODERN]),
  T("atlas-amber", "Atlas · Amber", LAYOUTS.ATLAS, "#D97706", [CATEGORY.MODERN, CATEGORY.SALES]),

  // === Meridian (two-column: sidebar + main) — 6 colors ===
  T("meridian-blue", "Meridian · Blue", LAYOUTS.MERIDIAN, "#1E6FE6", [CATEGORY.MODERN, CATEGORY.IT], { pages: 2, exp: ["mid", "senior"], industries: ["Technology"] }),
  T("meridian-navy", "Meridian · Navy", LAYOUTS.MERIDIAN, "#0F172A", [CATEGORY.EXEC, CATEGORY.FINANCE], { pages: 2, exp: ["senior"], industries: ["Banking", "Consulting"] }),
  T("meridian-teal", "Meridian · Teal", LAYOUTS.MERIDIAN, "#0EA5A2", [CATEGORY.MODERN, CATEGORY.HR], { pages: 2 }),
  T("meridian-plum", "Meridian · Plum", LAYOUTS.MERIDIAN, "#7C3AED", [CATEGORY.CREATIVE, CATEGORY.SALES], { pages: 2 }),
  T("meridian-emerald", "Meridian · Emerald", LAYOUTS.MERIDIAN, "#059669", [CATEGORY.MODERN, CATEGORY.SALES], { pages: 2 }),
  T("meridian-charcoal", "Meridian · Charcoal", LAYOUTS.MERIDIAN, "#111827", [CATEGORY.EXEC, CATEGORY.MODERN], { pages: 2 }),

  // === Keystone (classic serif, centered) — ATS ===
  T("keystone-classic", "Keystone · Classic", LAYOUTS.KEYSTONE, "#0F172A", [CATEGORY.ATS, CATEGORY.FINANCE, CATEGORY.HR], { font: F.serif }),
  T("keystone-navy", "Keystone · Navy", LAYOUTS.KEYSTONE, "#1e3a8a", [CATEGORY.ATS, CATEGORY.FINANCE], { font: F.serif }),
  T("keystone-forest", "Keystone · Forest", LAYOUTS.KEYSTONE, "#166534", [CATEGORY.ATS, CATEGORY.FINANCE], { font: F.serif }),
  T("keystone-burgundy", "Keystone · Burgundy", LAYOUTS.KEYSTONE, "#9F1239", [CATEGORY.ATS, CATEGORY.EXEC], { font: F.serif }),

  // === Apex (bold header block, exec) ===
  T("apex-navy", "Apex · Navy", LAYOUTS.APEX, "#0F172A", [CATEGORY.EXEC, CATEGORY.MODERN], { exp: ["senior"], font: F.mixed }),
  T("apex-black", "Apex · Black", LAYOUTS.APEX, "#000000", [CATEGORY.EXEC], { exp: ["senior"], font: F.mixed }),
  T("apex-blue", "Apex · Blue", LAYOUTS.APEX, "#1E6FE6", [CATEGORY.MODERN, CATEGORY.SALES], { font: F.mixed }),
  T("apex-emerald", "Apex · Emerald", LAYOUTS.APEX, "#065F46", [CATEGORY.EXEC, CATEGORY.FINANCE], { exp: ["senior"], font: F.mixed }),
  T("apex-plum", "Apex · Plum", LAYOUTS.APEX, "#6D28D9", [CATEGORY.MODERN, CATEGORY.SALES], { font: F.mixed }),

  // === Canvas (mono headings, technical) ===
  T("canvas-blue", "Canvas · Blue", LAYOUTS.CANVAS, "#1E6FE6", [CATEGORY.IT, CATEGORY.MODERN], { font: F.mono, industries: ["Data", "Engineering"] }),
  T("canvas-emerald", "Canvas · Emerald", LAYOUTS.CANVAS, "#059669", [CATEGORY.IT], { font: F.mono }),
  T("canvas-orange", "Canvas · Orange", LAYOUTS.CANVAS, "#EA580C", [CATEGORY.IT, CATEGORY.CREATIVE], { font: F.mono }),
  T("canvas-black", "Canvas · Ink", LAYOUTS.CANVAS, "#0F172A", [CATEGORY.IT, CATEGORY.ATS], { font: F.mono, ats: true }),

  // === Prism (creative sidebar + photo) ===
  T("prism-plum", "Prism · Plum", LAYOUTS.PRISM, "#7C3AED", [CATEGORY.CREATIVE], { pages: 2, photo: true }),
  T("prism-teal", "Prism · Teal", LAYOUTS.PRISM, "#0EA5A2", [CATEGORY.CREATIVE, CATEGORY.MODERN], { pages: 2, photo: true }),
  T("prism-coral", "Prism · Coral", LAYOUTS.PRISM, "#EF4444", [CATEGORY.CREATIVE], { pages: 2, photo: true }),
  T("prism-blue", "Prism · Blue", LAYOUTS.PRISM, "#1E6FE6", [CATEGORY.CREATIVE, CATEGORY.MODERN], { pages: 2, photo: true }),

  // === Signal (top ribbon) — freshers / punchy ===
  T("signal-blue", "Signal · Blue", LAYOUTS.SIGNAL, "#1E6FE6", [CATEGORY.FRESHER, CATEGORY.MODERN], { exp: ["entry", "mid"] }),
  T("signal-emerald", "Signal · Emerald", LAYOUTS.SIGNAL, "#059669", [CATEGORY.FRESHER, CATEGORY.HR], { exp: ["entry"] }),
  T("signal-plum", "Signal · Plum", LAYOUTS.SIGNAL, "#7C3AED", [CATEGORY.FRESHER, CATEGORY.CREATIVE], { exp: ["entry"] }),
  T("signal-teal", "Signal · Teal", LAYOUTS.SIGNAL, "#0EA5A2", [CATEGORY.FRESHER, CATEGORY.MODERN], { exp: ["entry", "mid"] }),

  // === Heritage (executive, formal serif, two-column) ===
  T("heritage-navy", "Heritage · Navy", LAYOUTS.HERITAGE, "#1e3a8a", [CATEGORY.EXEC, CATEGORY.FINANCE], { pages: 2, exp: ["senior"], font: F.serif }),
  T("heritage-charcoal", "Heritage · Charcoal", LAYOUTS.HERITAGE, "#111827", [CATEGORY.EXEC, CATEGORY.HR], { pages: 2, exp: ["senior"], font: F.serif }),
  T("heritage-burgundy", "Heritage · Burgundy", LAYOUTS.HERITAGE, "#9F1239", [CATEGORY.EXEC], { pages: 2, exp: ["senior"], font: F.serif }),

  // === Pulse (compact one-page, ATS-friendly) ===
  T("pulse-blue", "Pulse · Blue", LAYOUTS.PULSE, "#1E6FE6", [CATEGORY.ATS, CATEGORY.MODERN]),
  T("pulse-slate", "Pulse · Slate", LAYOUTS.PULSE, "#475569", [CATEGORY.ATS, CATEGORY.MODERN]),
  T("pulse-navy", "Pulse · Navy", LAYOUTS.PULSE, "#1e3a8a", [CATEGORY.ATS, CATEGORY.FINANCE]),
  T("pulse-teal", "Pulse · Teal", LAYOUTS.PULSE, "#0EA5A2", [CATEGORY.ATS, CATEGORY.HR]),
  T("pulse-emerald", "Pulse · Emerald", LAYOUTS.PULSE, "#059669", [CATEGORY.ATS, CATEGORY.MODERN]),

  // === Orbit (elegant centered, photo circle, UAE/GCC) ===
  T("orbit-navy", "Orbit · Navy", LAYOUTS.ORBIT, "#0F172A", [CATEGORY.GCC, CATEGORY.EXEC], { photo: true, industries: ["Banking", "Real Estate"], font: F.mixed }),
  T("orbit-blue", "Orbit · Blue", LAYOUTS.ORBIT, "#1E6FE6", [CATEGORY.GCC, CATEGORY.MODERN], { photo: true, industries: ["Hospitality", "Retail"], font: F.mixed }),
  T("orbit-teal", "Orbit · Teal", LAYOUTS.ORBIT, "#0EA5A2", [CATEGORY.GCC, CATEGORY.HR], { photo: true, industries: ["HR", "Admin"], font: F.mixed }),
  T("orbit-plum", "Orbit · Plum", LAYOUTS.ORBIT, "#7C3AED", [CATEGORY.GCC, CATEGORY.CREATIVE], { photo: true, font: F.mixed }),
  T("orbit-gold", "Orbit · Gold", LAYOUTS.ORBIT, "#B45309", [CATEGORY.GCC, CATEGORY.EXEC], { photo: true, exp: ["senior"], font: F.mixed }),

  // === Industry-specific extras ===
  T("apex-hr", "HR Lead", LAYOUTS.APEX, "#0EA5A2", [CATEGORY.HR, CATEGORY.MODERN], { industries: ["HR"], font: F.mixed }),
  T("canvas-data", "Data Scientist", LAYOUTS.CANVAS, "#0EA5A2", [CATEGORY.IT], { font: F.mono, industries: ["Data", "ML"] }),
  T("meridian-sales", "Sales Leader", LAYOUTS.MERIDIAN, "#DC2626", [CATEGORY.SALES, CATEGORY.EXEC], { pages: 2, exp: ["senior"] }),
  T("atlas-finance", "Finance Analyst", LAYOUTS.ATLAS, "#1e3a8a", [CATEGORY.FINANCE, CATEGORY.ATS], { industries: ["Banking"] }),
  T("keystone-lawyer", "Legal Counsel", LAYOUTS.KEYSTONE, "#4c1d95", [CATEGORY.ATS, CATEGORY.EXEC], { exp: ["senior"], font: F.serif, industries: ["Legal"] }),
  T("pulse-fresher", "Graduate", LAYOUTS.PULSE, "#059669", [CATEGORY.FRESHER, CATEGORY.ATS], { exp: ["entry"] }),
];

export const CATEGORIES = Object.values(CATEGORY);
export const EXPERIENCE_LEVELS = ["entry", "mid", "senior"];
export const PAGE_OPTIONS = [1, 2];

export function findTemplate(id) {
  return TEMPLATES.find((t) => t.id === id) || TEMPLATES[0];
}

export function filterTemplates({ category, expLevel, atsOnly, pages, industry }) {
  return TEMPLATES.filter((t) => {
    if (category && category !== "All" && !t.categories.includes(category)) return false;
    if (expLevel && !t.experience_levels.includes(expLevel)) return false;
    if (atsOnly && !t.ats_friendly) return false;
    if (pages && t.pages !== pages) return false;
    if (industry && industry !== "All") {
      const hay = (t.industries || []).join(" ").toLowerCase();
      if (!hay.includes(industry.toLowerCase())) return false;
    }
    return true;
  });
}

export const DEFAULT_TEMPLATE_ID = "atlas-blue";
